/**
 * Floating order panel (desktop only)
 *
 * The same trading controls as the action bar — quantity, OCO, buy/sell/flat —
 * in a draggable panel you can park next to price instead of reaching to the
 * top of the screen.
 *
 * Renders `ActionBarTrading` directly rather than reimplementing the controls.
 * A second copy of order-entry UI is exactly the kind of thing that drifts and
 * then places the wrong order, so there is only one implementation.
 *
 * Desktop only, deliberately: the action bar is already thumb-reachable on
 * mobile, and a floating panel there just covers the chart.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GripHorizontal } from 'lucide-react';
import ActionBarTrading from './ActionBarTrading';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  useAutoBotGlobalEnabled,
  useAutoBotStore,
} from '../store/autoBotStore';
import './FloatingOrderPanel.css';

interface FloatingOrderPanelProps {
  symbol: string;
  currentPrice: number;
  /** Bar interval, forwarded so candle-close scheduling matches the chart. */
  intervalMinutes?: number;
}

const POS_KEY = 'floatingOrderPanel.pos';

interface Pos {
  x: number;
  y: number;
}

function loadPos(): Pos {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Pos;
      if (Number.isFinite(p.x) && Number.isFinite(p.y)) return p;
    }
  } catch {
    // fall through to the default
  }
  return { x: 120, y: 160 };
}

/** Keep the panel on screen — a window resize shouldn't strand it off-viewport. */
function clampToViewport(p: Pos, el: HTMLElement | null): Pos {
  const w = el?.offsetWidth ?? 320;
  const h = el?.offsetHeight ?? 60;
  return {
    x: Math.min(Math.max(p.x, 0), Math.max(window.innerWidth - w, 0)),
    y: Math.min(Math.max(p.y, 0), Math.max(window.innerHeight - h, 0)),
  };
}

const FloatingOrderPanel: React.FC<FloatingOrderPanelProps> = ({
  symbol,
  currentPrice,
  intervalMinutes,
}) => {
  const isMobile = useIsMobile();
  const armed = useAutoBotGlobalEnabled();
  // useAutoBotActiveBots returns a COUNT, not the bots — take the list from
  // the store so the tooltip can name them.
  //
  // Select the STORED array and filter in a memo. Filtering inside the selector
  // builds a new array on every call, and zustand compares snapshots with
  // Object.is — so the store looked changed on every render, which re-rendered,
  // which ran the selector again. That is React #185 "Maximum update depth
  // exceeded", and it took the whole chart down via the ChartView boundary.
  const bots = useAutoBotStore((st) => st.bots);
  const activeBots = useMemo(() => bots.filter((b) => b.enabled), [bots]);
  const [pos, setPos] = useState<Pos>(loadPos);
  const ref = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      drag.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    []
  );

  useEffect(() => {
    const move = (e: PointerEvent): void => {
      if (!drag.current) return;
      setPos(
        clampToViewport(
          { x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy },
          ref.current
        )
      );
    };
    const up = (): void => {
      if (!drag.current) return;
      drag.current = null;
      // Persist only on release — writing on every move thrashes storage.
      setPos((p) => {
        try {
          localStorage.setItem(POS_KEY, JSON.stringify(p));
        } catch {
          // storage blocked — position just won't survive a reload
        }
        return p;
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, []);

  // Clamp on mount as well as on resize. The saved position is restored blind
  // from localStorage, so a position saved on a wider monitor (or before the
  // window shrank) puts the panel off-screen and it reads as "gone" — with no
  // toggle left to bring it back. Runs once, after layout, so ref.current has
  // real dimensions to clamp against.
  useEffect(() => {
    const reclamp = (): void => setPos((p) => clampToViewport(p, ref.current));
    reclamp();
    window.addEventListener('resize', reclamp);

    // The panel also has to re-clamp when IT changes size, not just the window.
    // Its width depends on what is showing — the ARMED chip and the bot-name
    // tooltip appear and disappear — so a panel parked near the right edge grows
    // straight off-screen and there is no toggle left to recover it.
    const ro = new ResizeObserver(reclamp);
    if (ref.current) ro.observe(ref.current);

    return () => {
      window.removeEventListener('resize', reclamp);
      ro.disconnect();
    };
  }, []);

  if (isMobile) return null;

  return (
    <div className="fop-panel" style={{ left: pos.x, top: pos.y }} ref={ref}>
      <div className="fop-grip" onPointerDown={onPointerDown} title="Drag to move">
        <GripHorizontal size={12} />
      </div>
      <div className="fop-controls">
        <ActionBarTrading symbol={symbol} currentPrice={currentPrice} intervalMinutes={intervalMinutes} />
      </div>
      {/* Arm/disarm lives with order entry: it's the same decision — whether
          orders go out — so it belongs where you place them, not on the chart
          where it competed with the overlay chips and got lost. */}
      <div className="fop-divider" />
      <div className="fop-arm-wrap">
        <button
          className={`fop-arm ${armed ? 'armed' : ''}`}
          onClick={() => useAutoBotStore.getState().setGlobalEnabled(!armed)}
          title={armed ? 'Bots ARMED — click to disarm' : 'Bots disarmed — click to arm'}
        >
          <span className="fop-arm-dot" />
          {armed ? 'ARMED' : 'DISARMED'}
        </button>
        {/* Which bots would actually fire. Armed with nothing enabled looks
            identical to armed-and-live otherwise. */}
        <div className="fop-bots-tip">
          {activeBots.length === 0 ? (
            <span className="fop-bots-none">no bots enabled</span>
          ) : (
            <>
              <span className="fop-bots-head">
                {activeBots.length} active bot{activeBots.length === 1 ? '' : 's'}
              </span>
              {activeBots.map((b) => (
                <span key={b.id} className="fop-bot-row">
                  {b.name}
                </span>
              ))}
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default FloatingOrderPanel;
